<script lang="ts">
  export interface GaugeChartSegment {
    label: string;
    value: number;
    color?: string;
  }

  const CHART_COLOR_TOKENS = [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
  ];

  const EMPTY_SEGMENT_COLOR = 'var(--color-gray-02)';
  const HALF_ANGLE_RANGE: [number, number] = [-Math.PI / 2, Math.PI / 2];

  const ANIMATION_DURATION_MS = 600;

  const valueFormatter = new Intl.NumberFormat('pt-BR');
  const percentFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  const HTML_ESCAPES: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  function escapeHtml(value: string): string {
    return value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
  }
</script>

<script setup lang="ts">
  import { computed } from 'vue';
  import { Donut } from '@unovis/ts';
  import { VisDonut, VisSingleContainer, VisTooltip } from '@unovis/vue';
  import { usePrefersReducedMotion } from '@/shared/lib';

  interface GaugeArc {
    data: GaugeChartSegment;
  }

  interface Props {
    segments: GaugeChartSegment[];
    centerValue: string;
    centerLabel?: string;
    size?: number;
    arcWidth?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    centerLabel: undefined,
    size: 180,
    arcWidth: undefined,
  });

  const prefersReducedMotion = usePrefersReducedMotion();
  const animationDuration = computed(() => (prefersReducedMotion.value ? 0 : ANIMATION_DURATION_MS));

  const total = computed(() => props.segments.reduce((sum, segment) => sum + segment.value, 0));
  const isEmpty = computed(() => total.value <= 0);

  const chartData = computed<GaugeChartSegment[]>(() =>
    isEmpty.value ? [{ label: '', value: 1 }] : props.segments
  );

  const chartHeight = computed(() => props.size / 2);
  const ringWidth = computed(() => props.arcWidth ?? (props.size * 20) / 180);
  const cornerRadius = computed(() => (ringWidth.value * 5) / 20);
  const padAngle = computed(() => (isEmpty.value ? 0 : 0.04));

  const legendItems = computed(() =>
    props.segments.map((segment, index) => ({
      label: segment.label,
      color: paletteColor(segment, index),
    }))
  );

  function paletteColor(segment: GaugeChartSegment, index: number): string {
    return segment.color ?? CHART_COLOR_TOKENS[index % CHART_COLOR_TOKENS.length];
  }

  const segmentValue = (segment: GaugeChartSegment) => segment.value;

  const segmentColor = (segment: GaugeChartSegment, index: number) =>
    isEmpty.value ? EMPTY_SEGMENT_COLOR : paletteColor(segment, index);

  function formatTooltip(segment: GaugeChartSegment): string {
    const percent = percentFormatter.format(segment.value / total.value);
    return `${escapeHtml(segment.label)}: ${valueFormatter.format(segment.value)} (${percent})`;
  }

  const tooltipTriggers = {
    [Donut.selectors.segment]: (arc: GaugeArc) => (isEmpty.value ? null : formatTooltip(arc.data)),
  };

  const chartDescription = computed(() => {
    const center = [props.centerValue, props.centerLabel].filter(Boolean).join(' ');
    if (isEmpty.value) return center;
    const segmentDescriptions = props.segments.map((segment) => {
      const percent = percentFormatter.format(segment.value / total.value);
      return `${segment.label}: ${valueFormatter.format(segment.value)} (${percent})`;
    });
    return `${center}. ${segmentDescriptions.join('; ')}`;
  });
</script>

<template>
  <div
    data-testid="gauge-chart"
    class="flex flex-col items-center gap-1"
  >
    <div
      role="img"
      :aria-label="chartDescription"
      class="relative"
      :style="{ width: `${props.size}px`, height: `${chartHeight}px` }"
    >
      <VisSingleContainer
        :data="chartData"
        :width="props.size"
        :height="chartHeight"
      >
        <VisTooltip :triggers="tooltipTriggers" />
        <VisDonut
          :value="segmentValue"
          :color="segmentColor"
          :arc-width="ringWidth"
          :angle-range="HALF_ANGLE_RANGE"
          :pad-angle="padAngle"
          :corner-radius="cornerRadius"
          :duration="animationDuration"
          :show-background="false"
        />
      </VisSingleContainer>
      <div
        class="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center text-center"
      >
        <p
          data-testid="gauge-chart-center-value"
          class="text-2xl/7 font-bold text-gray-07"
        >
          {{ props.centerValue }}
        </p>
        <p
          v-if="props.centerLabel"
          data-testid="gauge-chart-center-label"
          class="text-xs/4 text-gray-03"
        >
          {{ props.centerLabel }}
        </p>
      </div>
    </div>
    <ul class="flex items-center gap-4">
      <li
        v-for="item in legendItems"
        :key="item.label"
        data-testid="gauge-chart-legend-item"
        class="flex items-center gap-2 p-0.5"
      >
        <span
          data-testid="gauge-chart-legend-dot"
          class="size-3 rounded-full"
          :style="{ backgroundColor: item.color }"
        />
        <span class="text-[10px]/5 text-gray-03">{{ item.label }}</span>
      </li>
    </ul>
  </div>
</template>
